import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, MailCheck, LifeBuoy } from 'lucide-react';
import PortalShell from '@/components/portal/PortalShell';
import { GiisdecMark } from '@/components/portal/BrandLogo';
import { authApi } from '@/lib/authApi';
import { Card, Field, Input, Button } from '@/components/eds';

/**
 * Public Forgot Password page — /forgot-password
 *
 * Security posture (account-enumeration safe):
 *  - After a well-formed submission, ALWAYS shows the same neutral message,
 *    regardless of whether the email exists or what geo-api returned.
 *  - Never surfaces "account not found", backend errors, tokens, or
 *    mail-delivery details.
 *  - Endpoint resolves to {BASE}/api/auth/forgot-password (never /api/api).
 *
 * WP2.1B: presentation migrated to EDS v1.0. Enumeration-safe logic unchanged.
 */
const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const validEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setClientError(null);
    const value = email.trim();
    if (!value) {
      setClientError('Please enter your email address.');
      return;
    }
    if (!validEmail(value)) {
      setClientError('Please enter a valid email address (e.g. name@example.com).');
      return;
    }
    setLoading(true);
    // Fire the recovery request; the response is intentionally NOT branched on
    // so account existence can never be inferred from this page.
    await authApi.forgotPassword(value).catch(() => null);
    setLoading(false);
    setSubmitted(true);
  };

  return (
    <PortalShell>
      <section className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-eds-bg py-12 px-4 font-eds-body">
        <Card className="w-full max-w-md p-8">
          <div className="flex items-center gap-2.5 mb-6">
            <GiisdecMark className="w-8 h-8 flex-shrink-0" />
            <div className="leading-tight">
              <div className="font-eds-display font-bold text-sm tracking-tight text-eds-fg">GIISDEC</div>
              <div className="text-[9px] uppercase tracking-widest text-eds-muted-fg">Investor Portal</div>
            </div>
          </div>
          {!submitted ? (
            <>
              <h1 className="font-eds-display text-2xl font-bold text-eds-fg">Forgot your password?</h1>
              <p className="text-sm text-eds-muted-fg mt-1 mb-6">
                Enter the email address associated with your GIISDEC portal account and we
                will send password reset instructions.
              </p>

              <form onSubmit={onSubmit} className="space-y-4" noValidate>
                <Field label="Email address" htmlFor="fp-email" error={clientError ?? undefined}>
                  <Input
                    id="fp-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-describedby={clientError ? 'fp-email-error' : undefined}
                    invalid={!!clientError}
                    leftIcon={<Mail className="w-4 h-4" aria-hidden="true" />}
                    placeholder="you@example.com"
                    autoComplete="email"
                    autoFocus
                  />
                </Field>

                <Button type="submit" block loading={loading}>
                  {loading ? 'Sending…' : 'Send reset instructions'}
                </Button>
              </form>
            </>
          ) : (
            <div role="status" aria-live="polite">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-eds-pill bg-eds-success/12 border border-eds-success/30">
                  <MailCheck className="w-5 h-5 text-eds-emerald-2" aria-hidden="true" />
                </span>
                <h1 className="font-eds-display text-xl font-bold text-eds-fg">Check your email</h1>
              </div>
              <p className="text-sm text-eds-fg">
                If an account exists for this email address, password reset instructions
                will be sent.
              </p>
              <div className="mt-4 text-sm text-eds-muted-fg bg-eds-surface-2 border border-eds-border rounded-eds-md p-3 space-y-1">
                <p className="font-medium text-eds-fg">Did not receive an email?</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Check your spam or junk folder.</li>
                  <li>Confirm you entered the email registered with GIISDEC.</li>
                  <li>Wait a few minutes, then submit the form again.</li>
                </ul>
              </div>
              <Button
                type="button"
                variant="secondary"
                block
                className="mt-4"
                onClick={() => { setSubmitted(false); setClientError(null); }}
              >
                Send again
              </Button>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-eds-border flex flex-col gap-2 text-sm">
            <Link to="/investor-login" className="inline-flex items-center gap-1.5 text-eds-emerald-2 font-medium hover:underline">
              <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Return to Sign In
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-1.5 text-eds-muted-fg hover:underline">
              <LifeBuoy className="w-4 h-4" aria-hidden="true" /> Contact GIISDEC support
            </Link>
          </div>
        </Card>
      </section>
    </PortalShell>
  );
};

export default ForgotPassword;
